import {
  getAgentCallContractorApprovalLabel,
  getArrangementLimitLabel,
  getCategoriesLabel,
  getHasDesignatedContractorLabel,
  getRequestMethodLabel,
} from "@/api/client/operation-rule"
import type { OwnerModels } from "@/api/client/owner"

// 現在値(operationRule)と変更履歴(operationRuleLogs の1件)は同じ設定項目を持つので、共通の形で受ける。
export type OperationRuleSnapshotView = Pick<
  OwnerModels.OwnerGet200OwnerOperationRulesItemOperationRuleLogsItem,
  | "agentCallContractorApproval"
  | "hasDesignatedContractor"
  | "arrangementLimit"
  | "designatedContractors"
>

// データ表示用のヘルパーコンポーネント
const InfoRow = ({ label, value }: { label: string; value: string | number }) => (
  <div className="grid grid-cols-[140px_1fr] gap-2 items-start">
    <span className="text-xs font-medium text-slate-500">{label}</span>
    <span className="text-sm text-slate-900 whitespace-pre-wrap">{value}</span>
  </div>
)

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="space-y-3">
    <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
    <div className="bg-slate-50 rounded-lg p-4 space-y-3">{children}</div>
  </div>
)

// 運用ルール1件(現在値 or 履歴スナップショット)の設定内容を表示する。
export function OperationRuleDetailBody({
  operationRule,
}: {
  operationRule: OperationRuleSnapshotView | null | undefined
}) {
  const designatedContractors = operationRule?.designatedContractors ?? []

  return (
    <div className="space-y-6">
      {/* 1. 運用ルール設定 */}
      <Section title="1. 運用ルール設定">
        <InfoRow
          label="AC業者手配"
          value={getAgentCallContractorApprovalLabel(operationRule?.agentCallContractorApproval)}
        />
        <InfoRow
          label="オーナー指定業者"
          value={getHasDesignatedContractorLabel(operationRule?.hasDesignatedContractor)}
        />
        <InfoRow
          label="手配上限金額"
          value={getArrangementLimitLabel(operationRule?.arrangementLimit)}
        />
      </Section>

      {/* 2. 指定業者情報 */}
      {designatedContractors.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-700">2. 指定業者情報</h3>
          {designatedContractors.map((contractor, index) => (
            <div
              key={`${index}-${contractor.name}`}
              className="bg-slate-50 rounded-lg p-4 space-y-3"
            >
              <div className="text-xs font-medium text-slate-600 mb-2">業者 {index + 1}</div>
              <InfoRow label="業者名" value={contractor.name} />
              <InfoRow label="担当者名" value={contractor.contactPerson || "-"} />
              <InfoRow label="カテゴリ" value={getCategoriesLabel(contractor.categories)} />
              {contractor.otherCategory && (
                <InfoRow label="その他カテゴリ" value={contractor.otherCategory} />
              )}
              <InfoRow label="連絡先" value={contractor.contactInfo} />
              <InfoRow label="依頼方法" value={getRequestMethodLabel(contractor.requestMethod)} />
              <InfoRow label="住所" value={contractor.address || "-"} />
              <InfoRow label="備考" value={contractor.note || "-"} />
            </div>
          ))}
        </div>
      )}

      {designatedContractors.length === 0 && (
        <div className="text-sm text-slate-500 bg-slate-50 rounded-lg p-4">
          指定業者は登録されていません
        </div>
      )}
    </div>
  )
}
